import { TranslateService } from '@ngx-translate/core';

import { FileUploadErrorCode, FileUploadErrorConfig, FileUploadTranslationsCode } from '../../models/enums/file-upload-error-code.enum';

export interface ErrorDetails {
    message: string;
    code: string;
}

export class FileUploadErrorHandler {
    constructor(private translate: TranslateService) {}

    parseErrorResponse(response: any): ErrorDetails {
        try {
            const parsed = this.tryParseResponse(response);

            if (this.isKnownErrorCode(parsed?.code)) {
                return {
                    message: this.translateMessage(parsed.code),
                    code: parsed.code
                };
            }

            return this.createUnknownError();
        } catch (error) {
            return this.createParseError();
        }
    }

    translateMessage(code: string): string {
        const errorConfig = FileUploadErrorConfig[code as FileUploadErrorCode];
        if (!errorConfig) {
            return 'An unknown error occurred';
        }

        const translation = this.translate.instant(errorConfig.translationCode);
        return translation !== errorConfig.translationCode
            ? translation
            : errorConfig.defaultMessage;
    }

    createUploadMessage(file: any): string {
        if (file.status === 'failed') {
            const failedMessage = this.translateMessage(FileUploadErrorCode.FailedToUpload);
            return `${file.name} - ${failedMessage}: ${file.errorMessage}`;
        }

        const successMessage = this.translateMessage(FileUploadErrorCode.SuccessfullyUploaded);
        return `${file.originalName} - ${successMessage}`;
    }

    handleLinkError(error: any): any {
        return {
            status: 'failed',
            errorMessage: error?.error?.message || 'Failed to generate private link',
            code: 'LINK_GENERATION_ERROR'
        };
    }

    private tryParseResponse(response: any): any {
        if (typeof response?.response === 'string') {
            return JSON.parse(response.response);
        }
        return response?.response || response;
    }

    private isKnownErrorCode(code: string): boolean {
        return !!code && Object.values(FileUploadErrorCode).includes(code as FileUploadErrorCode);
    }

    private createUnknownError(): ErrorDetails {
        return {
            message: this.translateMessage(FileUploadTranslationsCode.UnknownError),
            code: 'UNKNOWN_ERROR'
        };
    }

    private createParseError(): ErrorDetails {
        return {
            message: this.translateMessage(FileUploadErrorCode.ParseError),
            code: 'PARSE_ERROR'
        };
    }
}
